import React from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Box, Card, CardContent, CardHeader, Flex, Heading, Text, Button, LoadingOrbit, CheckCircle, Warning } from '@chatsdet/ui';
import { SlackLogo } from '@phosphor-icons/react';
import { API_URL } from '../../config.js';
import { useAuth } from '../../contexts/AuthContext';
import { BRAND } from '../../brand';
import { toApiError } from '../../lib/api-error';

type PreviewResponse =
  | { ok: true; slackTeamId: string; slackUserId: string; expiresAt: number }
  | { ok: false; reason: 'invalid' | 'expired' };

type ConfirmResponse = { ok: true; slackTeamId: string; slackUserId: string };

export async function confirmSlackLink(token: string): Promise<ConfirmResponse> {
  const res = await fetch(`${API_URL}/integrations/slack/link/confirm`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ token }),
  });

  if (!res.ok) {
    throw await toApiError(res, 'Failed to link Slack account');
  }

  const data = (await res.json()) as ConfirmResponse | { ok: false; error?: string };
  if (!data.ok) {
    throw new Error(data.error || 'Failed to link Slack account');
  }

  return data;
}

/**
 * Confirmation page for the in-Slack "link your account" flow.
 *
 * The bot DMs a URL like `/integrations/slack/link?token=...`. This page:
 *  1. Previews the token server-side (unauthenticated) to show what's being linked
 *  2. Requires the user to be signed in to confirm
 *  3. POSTs the token to /link/confirm, which creates the Slack↔app-user row
 */
export function SlackLinkPage() {
  const search = useSearch({ strict: false }) as { token?: string };
  const token = search.token ?? '';
  const navigate = useNavigate();
  const { user } = useAuth();
  const name = BRAND.name;

  const preview = useQuery({
    queryKey: ['slack-link-preview', token],
    queryFn: async (): Promise<PreviewResponse> => {
      const res = await fetch(
        `${API_URL}/integrations/slack/link/preview?token=${encodeURIComponent(token)}`,
        { credentials: 'include' }
      );
      return res.json();
    },
    enabled: token.length > 0,
  });

  const confirm = useMutation({
    mutationFn: () => confirmSlackLink(token),
  });

  if (!token) {
    return (
      <CenterCard icon="warn" title="Missing link token">
        <Text size="sm" className="text-muted-foreground">
          The link is missing or malformed. Try asking the bot again — it will send you a
          fresh link.
        </Text>
      </CenterCard>
    );
  }

  if (preview.isLoading) {
    return (
      <CenterCard icon="slack" title="Verifying link…">
        <Flex align="center" justify="center" className="py-4">
          <LoadingOrbit size="md" />
        </Flex>
      </CenterCard>
    );
  }

  if (!preview.data || preview.data.ok === false) {
    const reason = preview.data?.ok === false ? preview.data.reason : 'invalid';
    return (
      <CenterCard icon="warn" title="This link can't be used">
        <Text size="sm" className="text-muted-foreground">
          {reason === 'expired'
            ? 'This link has expired. Ask the bot again for a fresh link — they expire 15 minutes after creation to keep things secure.'
            : 'This link is invalid or has already been used. Ask the bot again for a fresh link.'}
        </Text>
      </CenterCard>
    );
  }

  if (!user) {
    const returnUrl = encodeURIComponent(`/integrations/slack/link?token=${token}`);
    return (
      <CenterCard icon="slack" title="Sign in to link your account">
        <Text size="sm" className="text-muted-foreground mb-4">
          To link Slack user <code>{preview.data.slackUserId}</code> in workspace{' '}
          <code>{preview.data.slackTeamId}</code> to your {name} account, please sign in
          first. Once signed in, you'll be returned here to confirm.
        </Text>
        <Button variant="primary" onPress={() => navigate({ to: `/login?next=${returnUrl}` })}>
          Sign in
        </Button>
      </CenterCard>
    );
  }

  if (confirm.isSuccess) {
    return (
      <CenterCard icon="check" title="Linked — you're all set">
        <Text size="sm" className="text-muted-foreground">
          From now on, tests, workflows, and tasks you kick off from Slack will be
          attributed to you. You can close this tab and head back to the Slack thread.
        </Text>
      </CenterCard>
    );
  }

  return (
    <CenterCard icon="slack" title="Link your Slack account">
      <Text size="sm" className="mb-2">
        You're linking Slack user{' '}
        <strong>
          <code>{preview.data.slackUserId}</code>
        </strong>{' '}
        in workspace <strong>{preview.data.slackTeamId}</strong> to your {name} account{' '}
        <strong>{user.email ?? user.name ?? user.id}</strong>.
      </Text>
      <Text size="sm" className="text-muted-foreground mb-4">
        After linking, exploratory tests, workflows, and agent tasks that you trigger from
        Slack will be recorded as run by you instead of a generic admin.
      </Text>
      <Flex gap="sm">
        <Button
          variant="primary"
          isDisabled={confirm.isPending}
          onPress={() => confirm.mutate()}
        >
          {confirm.isPending ? 'Linking…' : 'Confirm link'}
        </Button>
        <Button variant="outline" onPress={() => window.close()}>
          Cancel
        </Button>
      </Flex>
      {confirm.isError && (
        <Flex align="center" gap="sm" className="mt-3 text-destructive">
          <Warning size={16} />
          <Text size="sm">
            {confirm.error instanceof Error ? confirm.error.message : 'Failed to link'}
          </Text>
        </Flex>
      )}
    </CenterCard>
  );
}

function CenterCard({
  icon,
  title,
  children,
}: {
  icon: 'slack' | 'check' | 'warn';
  title: string;
  children: React.ReactNode;
}) {
  const IconComponent =
    icon === 'check' ? CheckCircle : icon === 'warn' ? Warning : SlackLogo;
  const iconClass =
    icon === 'check'
      ? 'text-success'
      : icon === 'warn'
        ? 'text-warning'
        : 'text-foreground';
  return (
    <Box className="flex items-center justify-center min-h-screen bg-muted/20 px-4">
      <Card padding="lg" className="max-w-md w-full">
        <CardHeader>
          <Flex align="center" gap="md">
            <div className="p-2 bg-muted/50 rounded-lg">
              <IconComponent size={24} className={iconClass} />
            </div>
            <Heading level={3}>{title}</Heading>
          </Flex>
        </CardHeader>
        <CardContent>{children}</CardContent>
      </Card>
    </Box>
  );
}
